import { z } from 'zod';
import { PositiveDecimalString, PaginationQuerySchema } from './common.js';

/**
 * OTC order book schemas.
 *
 * Users place UO `buy` orders (paying AP) or UO `sell` orders (receiving AP).
 * The matching engine runs inside the same request transaction as the
 * placement: a new order walks the opposite side of the book and settles
 * against any resting order with a compatible unit price until filled.
 *
 * `unit_price` is quoted as AP per 1 UO. `amount_uo` is the maximum UO the
 * user is willing to trade at this price. `amount_ap` is derived at the
 * service layer and is never taken from the client.
 *
 * Field naming is deliberately divergent from the legacy `uo_bonus_log`
 * column names (`type=1/2`, `ruin_ep`, `sell_type`) — the new schema keeps
 * the same lock/settle/fee semantics but uses self-documenting fields.
 */

export const OTC_SIDE = ['buy', 'sell'] as const;
export const OTC_STATUS = ['open', 'partial', 'done', 'cancelled'] as const;

export const PlaceOtcOrderSchema = z.object({
  side: z.enum(OTC_SIDE),
  amount_uo: PositiveDecimalString,
  unit_price: PositiveDecimalString,
});
export type PlaceOtcOrderRequest = z.infer<typeof PlaceOtcOrderSchema>;

export const CancelOtcOrderSchema = z.object({
  order_id: z.coerce.number().int().positive(),
});
export type CancelOtcOrderRequest = z.infer<typeof CancelOtcOrderSchema>;

export const OtcOrderListQuerySchema = PaginationQuerySchema.extend({
  side: z.enum(OTC_SIDE).optional(),
  status: z.enum(OTC_STATUS).optional(),
  /** Admin-only: restrict by user id. Ignored for user-facing calls. */
  user_id: z.coerce.number().int().positive().optional(),
});
export type OtcOrderListQuery = z.infer<typeof OtcOrderListQuerySchema>;

export const OtcTradeListQuerySchema = PaginationQuerySchema.extend({
  user_id: z.coerce.number().int().positive().optional(),
  order_id: z.coerce.number().int().positive().optional(),
});
export type OtcTradeListQuery = z.infer<typeof OtcTradeListQuerySchema>;

/** Order-book snapshot query for the trading hall. */
export const OtcBookQuerySchema = z.object({
  depth: z.coerce.number().int().min(1).max(100).default(20),
});
export type OtcBookQuery = z.infer<typeof OtcBookQuerySchema>;

/** UO交易大廳 — paginated order cards (public feed). */
export const OtcHallQuerySchema = PaginationQuerySchema.extend({
  side: z.enum(OTC_SIDE).optional(),
  status: z.enum(OTC_STATUS).optional(),
});
export type OtcHallQuery = z.infer<typeof OtcHallQuerySchema>;

/** Admin force-cancel. */
export const AdminCancelOtcOrderSchema = z.object({
  order_id: z.coerce.number().int().positive(),
  reason: z.string().min(1).max(200),
});
export type AdminCancelOtcOrderRequest = z.infer<typeof AdminCancelOtcOrderSchema>;
